/**
 * Design Guidelines Abstraction Layer
 *
 * Unified token interface that both Apple Liquid Glass and Google Material Design 3
 * implement. Theme construction and Glass components consume tokens from this interface
 * rather than directly importing guideline-specific modules.
 *
 * Design guideline (apple vs material) is orthogonal to color mode (light/dark/midnight/twilight).
 * A user can use Material + Dark or Apple + Twilight, etc.
 */

import type { ThemeOptions } from '@mui/material/styles';
import { liquidGlass, liquidRadius, liquidShadows, liquidShadowsDark, liquidSpacing } from './liquidGlass';
import { materialMotion, materialRadius, materialShadows, materialSpacing, materialSurface } from './materialDesign';
import { materialTypographyVariants } from './materialTypography';
import { typographyVariants } from './typography';

// ============================================================================
// Types
// ============================================================================

export type DesignGuideline = 'apple' | 'material';

export interface GuidelineRadius {
  /** Smallest — chips, tags, small badges */
  xs: number;
  /** Small — buttons, inputs */
  sm: number;
  /** Medium — cards, panels */
  md: number;
  /** Large — modals, large surfaces */
  lg: number;
  /** Extra large — dialogs, drawers */
  xl: number;
  /** Full — pill shapes */
  full: number;
}

export interface GuidelineSurface {
  light: {
    background: string;
    backgroundHover: string;
    border: string;
    borderHover: string;
    shadow: string;
    shadowHover?: string;
  };
  dark: {
    background: string;
    backgroundHover: string;
    border: string;
    borderHover: string;
    shadow: string;
    shadowHover?: string;
  };
}

export interface GuidelineShadows {
  0: string;
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
}

export interface GuidelineSpacing {
  xxs: number;
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export interface GuidelineMotion {
  /** Standard easing curve */
  easing: string;
  /** Entry easing */
  easingEnter: string;
  /** Exit easing */
  easingExit: string;
  /** Short duration (ms) */
  durationShort: number;
  /** Medium duration (ms) */
  durationMedium: number;
  /** Long duration (ms) */
  durationLong: number;
}

export interface GuidelineTokens {
  /** Guideline identifier */
  guideline: DesignGuideline;

  /** Border radius scale */
  radius: GuidelineRadius;

  /** Typography options for MUI */
  // biome-ignore lint/suspicious/noExplicitAny: MUI typography options use complex union types
  typography: Record<string, any>;

  /** Surface system — primary surfaces (cards, panels) */
  surface: GuidelineSurface;

  /** Shadow scale for elevation levels */
  shadows: GuidelineShadows;

  /** Dark-mode shadow scale (Apple has separate dark shadows) */
  shadowsDark: GuidelineShadows;

  /** Spacing scale */
  spacing: GuidelineSpacing;

  /** Motion/animation tokens */
  motion: GuidelineMotion;

  /** Whether surfaces use backdrop-filter blur */
  usesBlur: boolean;

  /** Whether surfaces have specular highlights */
  usesSpecular: boolean;

  /** MUI component override overrides specific to this guideline */
  componentOverrides: ThemeOptions['components'];
}

// ============================================================================
// Apple Liquid Glass Tokens
// ============================================================================

const appleTokens: GuidelineTokens = {
  guideline: 'apple',

  radius: {
    xs: liquidRadius.xs, // 8
    sm: liquidRadius.sm, // 12
    md: liquidRadius.md, // 16
    lg: liquidRadius.lg, // 20
    xl: liquidRadius.xl, // 24
    full: liquidRadius.pill, // 9999
  },

  typography: typographyVariants,

  surface: {
    light: {
      background: liquidGlass.regular.light.background,
      backgroundHover: liquidGlass.regular.light.backgroundHover,
      border: liquidGlass.regular.light.border,
      borderHover: liquidGlass.regular.light.borderHover,
      shadow: liquidGlass.regular.light.shadow,
      shadowHover: liquidGlass.regular.light.shadowHover,
    },
    dark: {
      background: liquidGlass.regular.dark.background,
      backgroundHover: liquidGlass.regular.dark.backgroundHover,
      border: liquidGlass.regular.dark.border,
      borderHover: liquidGlass.regular.dark.borderHover,
      shadow: liquidGlass.regular.dark.shadow,
      shadowHover: liquidGlass.regular.dark.shadowHover,
    },
  },

  shadows: liquidShadows,
  shadowsDark: liquidShadowsDark,

  spacing: liquidSpacing,

  motion: {
    easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    easingEnter: 'cubic-bezier(0, 0, 0.2, 1)',
    easingExit: 'cubic-bezier(0.4, 0, 1, 1)',
    durationShort: 150,
    durationMedium: 300,
    durationLong: 500,
  },

  usesBlur: true,
  usesSpecular: true,

  componentOverrides: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: liquidRadius.pill, // Pill buttons
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: liquidRadius.md, // 16px
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: liquidRadius.lg, // 20px
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: liquidRadius.xs, // 8px
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: liquidRadius.sm, // 12px
        },
      },
    },
  },
};

// ============================================================================
// Material Design 3 Tokens
// ============================================================================

const materialTokens: GuidelineTokens = {
  guideline: 'material',

  radius: {
    xs: materialRadius.xs, // 0
    sm: materialRadius.sm, // 4
    md: materialRadius.md, // 8
    lg: materialRadius.lg, // 12
    xl: materialRadius.xl, // 16
    full: materialRadius.full, // 9999
  },

  typography: materialTypographyVariants,

  surface: {
    light: {
      background: materialSurface.regular.light.background,
      backgroundHover: materialSurface.regular.light.backgroundHover,
      border: materialSurface.regular.light.border,
      borderHover: materialSurface.regular.light.borderHover,
      shadow: materialSurface.regular.light.shadow,
      shadowHover: materialSurface.regular.light.shadowHover,
    },
    dark: {
      background: materialSurface.regular.dark.background,
      backgroundHover: materialSurface.regular.dark.backgroundHover,
      border: materialSurface.regular.dark.border,
      borderHover: materialSurface.regular.dark.borderHover,
      shadow: materialSurface.regular.dark.shadow,
      shadowHover: materialSurface.regular.dark.shadowHover,
    },
  },

  shadows: materialShadows,
  shadowsDark: materialShadows, // MD3 uses same shadow values for light/dark

  spacing: materialSpacing,

  motion: {
    easing: materialMotion.standard,
    easingEnter: materialMotion.standardDecelerate,
    easingExit: materialMotion.standardAccelerate,
    durationShort: materialMotion.durationShort4,
    durationMedium: materialMotion.durationMedium2,
    durationLong: materialMotion.durationLong2,
  },

  usesBlur: false,
  usesSpecular: false,

  componentOverrides: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20, // MD3 rounded rectangle, not pill
          '&:hover': {
            transform: 'none', // MD3 doesn't use translateY on hover
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: materialRadius.lg, // 12px
          backdropFilter: 'none',
          '&:hover': {
            transform: 'none', // MD3 doesn't lift cards on hover
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: materialRadius.xxl, // 28px — MD3 standard
          backdropFilter: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: materialRadius.sm, // 8px
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8, // MD3 text fields
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: materialRadius.sm, // 4px — MD3 menus
          backdropFilter: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: materialRadius.lg, // 12px
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: materialRadius.lg, // 12px
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: materialRadius.sm, // 4px
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: materialRadius.sm, // 4px
        },
      },
    },
  },
};

// ============================================================================
// Factory
// ============================================================================

const guidelineMap: Record<DesignGuideline, GuidelineTokens> = {
  apple: appleTokens,
  material: materialTokens,
};

/**
 * Get the design tokens for a given guideline.
 *
 * @param guideline - 'apple' for Liquid Glass, 'material' for MD3
 * @returns The full set of guideline tokens
 */
export function getGuidelineTokens(guideline: DesignGuideline): GuidelineTokens {
  return guidelineMap[guideline];
}

/**
 * Get the stored design guideline preference.
 */
export function getStoredDesignGuideline(): DesignGuideline | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('design-guideline');
  if (stored === 'apple' || stored === 'material') return stored;
  return null;
}

/**
 * Store design guideline preference.
 */
export function storeDesignGuideline(guideline: DesignGuideline): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('design-guideline', guideline);
}

/**
 * Get initial design guideline (stored > 'apple' default).
 */
export function getInitialDesignGuideline(): DesignGuideline {
  return getStoredDesignGuideline() ?? 'apple';
}

/**
 * All available design guidelines.
 */
export const availableDesignGuidelines: DesignGuideline[] = ['apple', 'material'];

/**
 * Display labels for design guidelines.
 */
export const designGuidelineLabels: Record<DesignGuideline, string> = {
  apple: 'Apple Liquid Glass',
  material: 'Material Design 3',
};

// ============================================================================
// MUI Theme Augmentation
// ============================================================================
// Exposes `theme.designGuideline` inside all styled() callbacks so Glass
// components can read the active guideline without additional hooks.

declare module '@mui/material/styles' {
  interface Theme {
    designGuideline: DesignGuideline;
  }
  interface ThemeOptions {
    designGuideline?: DesignGuideline;
  }
}
